import React, { createContext, useCallback, useContext, useRef } from "react";
import type { SaveStatus } from "campaign-builder/shell/SummaryCard";

export type { SaveStatus };

interface SaveStatusApi {
  reportSaving: () => void;
  reportSaved: () => void;
  reportError: () => void;
}

const noop = () => undefined;
const SaveStatusContext = createContext<SaveStatusApi>({ reportSaving: noop, reportSaved: noop, reportError: noop });

export const useSaveStatus = () => useContext(SaveStatusContext);

export const SaveStatusProvider = ({
  children,
  onStatus,
}: {
  children: React.ReactNode;
  onStatus: (status: SaveStatus) => void;
}) => {
  const pending = useRef(0);
  const failed = useRef(false);

  const reportSaving = useCallback(() => {
    if (pending.current === 0) failed.current = false;
    pending.current += 1;
    onStatus("saving");
  }, [onStatus]);

  const reportSaved = useCallback(() => {
    pending.current = Math.max(0, pending.current - 1);
    if (pending.current === 0) onStatus(failed.current ? "error" : "saved");
  }, [onStatus]);

  const reportError = useCallback(() => {
    failed.current = true;
    pending.current = Math.max(0, pending.current - 1);
    if (pending.current === 0) onStatus("error");
  }, [onStatus]);

  const value = React.useMemo(
    () => ({ reportSaving, reportSaved, reportError }),
    [reportSaving, reportSaved, reportError]
  );

  return <SaveStatusContext.Provider value={value}>{children}</SaveStatusContext.Provider>;
};

export const trackSave = async (api: SaveStatusApi, action: () => Promise<unknown>) => {
  api.reportSaving();
  try {
    await action();
    api.reportSaved();
  } catch (error) {
    api.reportError();
    throw error;
  }
};
